import React, { useCallback, useEffect, useRef, useState } from 'react';
import Markdown from '../recording/Markdown';
import { announcementsApi, announceChanged, errorOf, day } from '../../lib/announcements';
import './Announcements.css';

/**
 * THE ANNOUNCEMENTS TAB under the bell (migration 125): every announcement
 * that is showing and is for this person, newest first, each marked unread
 * until opened. Opening one draws its text (components/recording/
 * Markdown.js, never HTML) and marks it read; an Important one not yet
 * acknowledged has its "I've read this" here too, the same as its banner.
 *
 * `focus` opens one at once - the bell's line for it was pressed. Viewing
 * as somebody, nothing is marked: their feed is shown as it is.
 */
export default function AnnouncementFeed({ token, focus = null, onCount }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(focus);
  const [busy, setBusy] = useState('');
  const seq = useRef(0);

  const load = useCallback(() => {
    const n = ++seq.current;
    return announcementsApi.mine(token)
      .then((r) => { if (n !== seq.current) return; setState(r); setError(''); })
      .catch((e) => { if (n === seq.current) setError(errorOf(e, 'Could not load the announcements.')); });
  }, [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener('jarvis:announcements-changed', load);
    return () => window.removeEventListener('jarvis:announcements-changed', load);
  }, [load]);
  useEffect(() => { if (focus) setOpen(focus); }, [focus]);
  useEffect(() => { if (state && onCount) onCount(state.unread || 0); }, [state, onCount]);

  const feed = state?.feed || [];
  const onBehalf = Boolean(state?.on_behalf);

  /** Opened: read, on the server and here - unless somebody is being viewed as. */
  const markRead = useCallback(async (a) => {
    if (!a || a.read || onBehalf) return;
    setState((s) => (s ? {
      ...s,
      feed: s.feed.map((x) => (x.id === a.id ? { ...x, read: true } : x)),
      unread: Math.max(0, (s.unread || 0) - (a.importance === 'important' ? 0 : 1))
    } : s));
    try {
      await announcementsApi.read(a.id, token);
      announceChanged();
    } catch { /* it stays unread on the server; the next look says so */ }
  }, [onBehalf, token]);

  useEffect(() => {
    if (!open || !state) return;
    markRead(feed.find((x) => x.id === open));
  }, [open, state]); // eslint-disable-line react-hooks/exhaustive-deps

  const acknowledge = async (a) => {
    setBusy(a.id);
    try {
      await announcementsApi.acknowledge(a.id, a.version, token);
      announceChanged();
      await load();
    } catch (e) {
      setError(errorOf(e, 'That did not save. Try again.'));
      load();
    }
    setBusy('');
  };

  if (!state && !error) return <p className="nb-empty" aria-busy="true">Loading…</p>;
  if (state && state.ready === false) return <p className="nb-empty">Announcements are not set up on this server yet.</p>;

  return (
    <div className="ann-feed" data-testid="announcement-feed">
      {error && <p className="nb-error" role="alert">{error}</p>}
      {onBehalf && <p className="ann-behalf">Viewing as somebody else: nothing you open here is marked read for them.</p>}
      {feed.length === 0 ? (
        <p className="nb-empty">No announcements just now.</p>
      ) : (
        <ul className="ann-feed-list">
          {feed.map((a) => {
            const unread = a.importance === 'important' ? !a.acknowledged : !a.read;
            const isOpen = open === a.id;
            return (
              <li key={a.id} className={`ann-feed-item${unread ? ' is-unread' : ''}`} data-announcement={a.id}>
                <button type="button" className="ann-feed-head" aria-expanded={isOpen}
                        onClick={() => setOpen(isOpen ? null : a.id)}>
                  <span className="ann-dot" aria-hidden="true" />
                  <span className="ann-feed-text">
                    <span className="ann-feed-title">
                      {a.importance === 'important' && <span className="ann-chip is-important">Important</span>}
                      {a.title}
                      {unread && <span className="ann-sr"> (unread)</span>}
                    </span>
                    <time className="nb-when" dateTime={a.starts_at}>{day(a.starts_at)}{a.edited ? ' · updated' : ''}</time>
                  </span>
                </button>
                {isOpen && (
                  <div className="ann-feed-body">
                    {String(a.body || '').trim() ? <Markdown text={a.body} className="ann-md" links /> : <p className="ann-quiet">No more to it than the title.</p>}
                    {a.importance === 'important' && !a.acknowledged && (
                      <button type="button" className="btn btn-primary btn-small" disabled={onBehalf || busy === a.id}
                              onClick={() => acknowledge(a)}>
                        {busy === a.id ? 'Saving…' : 'I’ve read this'}
                      </button>
                    )}
                    {a.importance === 'important' && a.acknowledged && <p className="ann-quiet">You acknowledged this on {day(a.acknowledged_at)}.</p>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
